/**
 * ConfirmDialog — 앱 안 확인창 (DESIGN-AI §13, 기준: 이벤트 삭제)
 * ----------------------------------------------------------------------------
 * window.confirm 대신 쓴다. ModalShell sm(420px) 위에 제목 + 설명 + 취소 / 실행.
 * 되돌릴 수 없는 동작이면 tone="danger" → 실행 버튼이 빨강.
 *
 * 설치: App 최상단을 <ConfirmProvider> 로 감싼다(ToastProvider 옆).
 * 사용:
 *   const confirm = useConfirm();
 *   const ask = usePrompt();   // window.prompt 대신 — 취소하면 null
 *   const name = await ask({ title: "폴더 추가", label: "폴더 이름", confirmLabel: "추가" });
 *   if (!(await confirm({ title: "이벤트 삭제", message: "되돌릴 수 없습니다.", confirmLabel: "삭제", tone: "danger" }))) return;
 */
import React from "react";
import ModalShell from "./ModalShell";
import Button from "./Button";
import { inputCls, labelCls } from "./formStyles";
import { useLanguage } from "./LanguageProvider";

export type ConfirmOptions = {
  title: string;
  message?: React.ReactNode;
  /** 실행 버튼 글자 — 동작 이름("삭제", "모두 삭제"). 기본 "확인" */
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: "default" | "danger";
};

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

export type PromptOptions = {
  title: string;
  /** 입력칸 라벨 */
  label?: string;
  message?: React.ReactNode;
  defaultValue?: string;
  placeholder?: string;
  confirmLabel?: string;
  cancelLabel?: string;
};
type PromptFn = (opts: PromptOptions) => Promise<string | null>;

const ConfirmContext = React.createContext<ConfirmFn | null>(null);
const PromptContext = React.createContext<PromptFn | null>(null);

/** window.prompt 대신 쓰는 앱 안 입력 모달. 취소·빈 값이면 null. */
export function usePrompt(): PromptFn {
  const ctx = React.useContext(PromptContext);
  if (ctx) return ctx;
  return async ({ title, defaultValue }) => {
    const v = window.prompt(title, defaultValue ?? "");
    return v && v.trim() ? v.trim() : null;
  };
}

export function useConfirm(): ConfirmFn {
  const ctx = React.useContext(ConfirmContext);
  if (ctx) return ctx;
  // Provider 가 없는 트리(테스트 등)에서는 브라우저 기본으로 물러난다 — 크래시보다 낫다.
  return async ({ title, message }) =>
    window.confirm([title, typeof message === "string" ? message : ""].filter(Boolean).join("\n\n"));
}

type Pending = ConfirmOptions & { resolve: (ok: boolean) => void };
type PendingPrompt = PromptOptions & { resolve: (v: string | null) => void };

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const { t } = useLanguage();
  const [pending, setPending] = React.useState<Pending | null>(null);
  const [pendingPrompt, setPendingPrompt] = React.useState<PendingPrompt | null>(null);
  const [promptValue, setPromptValue] = React.useState("");

  const confirm = React.useCallback<ConfirmFn>(
    (opts) => new Promise<boolean>((resolve) => setPending({ ...opts, resolve })),
    []
  );
  const prompt = React.useCallback<PromptFn>(
    (opts) => new Promise<string | null>((resolve) => { setPromptValue(opts.defaultValue ?? ""); setPendingPrompt({ ...opts, resolve }); }),
    []
  );

  const close = (ok: boolean) => {
    pending?.resolve(ok);
    setPending(null);
  };
  const closePrompt = (value: string | null) => {
    pendingPrompt?.resolve(value && value.trim() ? value.trim() : null);
    setPendingPrompt(null);
  };

  return (
    <PromptContext.Provider value={prompt}>
    <ConfirmContext.Provider value={confirm}>
      {children}
      {pending ? (
        <ModalShell
          open
          size="sm"
          elevated
          onClose={() => close(false)}
          title={pending.title}
          footer={
            <>
              <Button variant="secondary" size="sm" onClick={() => close(false)}>{pending.cancelLabel || t("취소", "Cancel")}</Button>
              <Button variant={pending.tone === "danger" ? "danger" : "primary"} size="sm" onClick={() => close(true)} autoFocus>
                {pending.confirmLabel || t("확인", "OK")}
              </Button>
            </>
          }
        >
          {pending.message ? <div className="whitespace-pre-line px-5 py-4 text-[13px] leading-relaxed text-gray-600">{pending.message}</div> : null}
        </ModalShell>
      ) : null}
      {pendingPrompt ? (
        <ModalShell
          open
          size="sm"
          elevated
          onClose={() => closePrompt(null)}
          title={pendingPrompt.title}
          footer={
            <>
              <Button variant="secondary" size="sm" onClick={() => closePrompt(null)}>{pendingPrompt.cancelLabel || t("취소", "Cancel")}</Button>
              <Button size="sm" disabled={!promptValue.trim()} onClick={() => closePrompt(promptValue)}>{pendingPrompt.confirmLabel || t("확인", "OK")}</Button>
            </>
          }
        >
          <form className="ds-scope grid gap-2 px-5 py-4" onSubmit={(e) => { e.preventDefault(); if (promptValue.trim()) closePrompt(promptValue); }}>
            {pendingPrompt.message ? <div className="text-[13px] leading-relaxed text-gray-600">{pendingPrompt.message}</div> : null}
            <label className="flex flex-col gap-1">
              {pendingPrompt.label ? <span className={labelCls}>{pendingPrompt.label}</span> : null}
              <input className={inputCls} value={promptValue} placeholder={pendingPrompt.placeholder}
                onChange={(e) => setPromptValue(e.target.value)} autoFocus />
            </label>
          </form>
        </ModalShell>
      ) : null}
    </ConfirmContext.Provider>
    </PromptContext.Provider>
  );
}
